import { Wifi, WifiOff } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

export const PresenceIndicator = ({ connected, onlineUsers, currentUserId }) => {
  const otherUsers = onlineUsers.filter(u => u.user_id !== currentUserId);

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button className="flex items-center gap-2 px-2 py-1 rounded-lg hover:bg-muted transition-colors" data-testid="presence-indicator">
          <div className={`w-2 h-2 rounded-full ${connected ? 'bg-emerald-500 animate-pulse' : 'bg-red-500'}`} />
          {connected ? (
            <div className="flex items-center gap-1">
              <div className="flex -space-x-2">
                {otherUsers.slice(0, 3).map(u => (
                  u.picture ? (
                    <img key={u.user_id} src={u.picture} alt={u.name} className="w-6 h-6 rounded-full border-2 border-background" />
                  ) : (
                    <div key={u.user_id} className="w-6 h-6 rounded-full border-2 border-background bg-primary/20 flex items-center justify-center text-[10px] font-bold text-primary">
                      {(u.name || '?')[0].toUpperCase()}
                    </div>
                  )
                ))}
              </div>
              {otherUsers.length > 0 && (
                <span className="text-xs text-muted-foreground ml-1">{otherUsers.length} online</span>
              )}
              {otherUsers.length === 0 && (
                <span className="text-xs text-muted-foreground">Only you</span>
              )}
            </div>
          ) : (
            <span className="text-xs text-muted-foreground flex items-center gap-1">
              <WifiOff className="h-3 w-3" /> Offline
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-3" align="end">
        <div className="space-y-3">
          <div className="flex items-center gap-2 text-sm font-semibold">
            <Wifi className={`h-4 w-4 ${connected ? 'text-emerald-500' : 'text-red-500'}`} />
            {connected ? 'Live Sync Active' : 'Reconnecting...'}
          </div>
          {onlineUsers.length > 0 && (
            <div className="space-y-2">
              <div className="text-xs text-muted-foreground font-medium uppercase tracking-wider">Online Now</div>
              {onlineUsers.map(u => (
                <div key={u.user_id} className="flex items-center gap-2.5">
                  <div className="relative">
                    {u.picture ? (
                      <img src={u.picture} alt={u.name} className="w-7 h-7 rounded-full" />
                    ) : (
                      <div className="w-7 h-7 rounded-full bg-primary/20 flex items-center justify-center text-xs font-bold text-primary">
                        {(u.name || '?')[0].toUpperCase()}
                      </div>
                    )}
                    <div className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 bg-emerald-500 rounded-full border-2 border-background" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">
                      {u.name || u.user_id}
                      {u.user_id === currentUserId && <Badge variant="secondary" className="ml-1.5 text-[10px] h-4">You</Badge>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export const EditingIndicator = ({ editingUsers, entityType, entityId }) => {
  const key = `${entityType}:${entityId}`;
  const editor = editingUsers[key];
  if (!editor) return null;

  return (
    <div className="flex items-center gap-1.5 text-xs text-amber-600 dark:text-amber-400 animate-pulse" data-testid="editing-indicator">
      <div className="w-1.5 h-1.5 rounded-full bg-amber-500" />
      <span>{editor.user_name} is editing...</span>
    </div>
  );
};
